import { AdminRO } from '@routellers/types/dist/admin/admin.ro';
import {
    ADMIN_SECTION_CATALOG,
    AdminCapabilityEnum,
    AdminPermissions,
    AdminSectionEnum,
    emptyAdminPermissions,
    fullAdminPermissions,
} from '@routellers/types/dist/admin/admin-permissions';

export {
    ADMIN_SECTION_CATALOG,
    AdminCapabilityEnum,
    AdminSectionEnum,
    emptyAdminPermissions,
    fullAdminPermissions,
};

export const adminCan = (
    admin: Pick<AdminRO, 'id' | 'totalAccess' | 'permissions'> | null | undefined,
    section: AdminSectionEnum,
    capability: AdminCapabilityEnum = AdminCapabilityEnum.VIEW,
): boolean => {
    if (!admin) return false;
    if (admin.id === 999999999 || admin.totalAccess) return true;
    if (admin.permissions == null) return true;

    const caps = admin.permissions.sections?.[section] || [];

    if (caps.includes(capability)) return true;
    if (
        capability === AdminCapabilityEnum.VIEW &&
        (caps.includes(AdminCapabilityEnum.EDIT) ||
            caps.includes(AdminCapabilityEnum.DELETE) ||
            caps.includes(AdminCapabilityEnum.TEXT))
    ) {
        return true;
    }
    if (capability === AdminCapabilityEnum.EDIT && caps.includes(AdminCapabilityEnum.DELETE)) {
        return true;
    }

    return false;
};

export const toggleCapability = (
    permissions: AdminPermissions,
    section: AdminSectionEnum,
    capability: AdminCapabilityEnum,
    enabled: boolean,
): AdminPermissions => {
    const current = new Set(permissions.sections[section] || []);

    if (enabled) {
        current.add(capability);
        if (
            capability === AdminCapabilityEnum.EDIT ||
            capability === AdminCapabilityEnum.DELETE ||
            capability === AdminCapabilityEnum.TEXT
        ) {
            current.add(AdminCapabilityEnum.VIEW);
        }
        if (capability === AdminCapabilityEnum.DELETE) {
            current.add(AdminCapabilityEnum.EDIT);
        }
    } else {
        current.delete(capability);
        if (capability === AdminCapabilityEnum.VIEW) {
            current.clear();
        }
        if (capability === AdminCapabilityEnum.EDIT) {
            current.delete(AdminCapabilityEnum.DELETE);
        }
    }

    const nextSections = { ...permissions.sections };

    if (current.size) {
        nextSections[section] = [...current];
    } else {
        delete nextSections[section];
    }

    return { ...permissions, sections: nextSections };
};

/** Text on routes without edit/delete. Mirrors the API helper. */
export const isRouteTextOnly = (
    admin: Pick<AdminRO, 'id' | 'totalAccess' | 'permissions'> | null | undefined,
): boolean => {
    if (!admin) return false;
    if (admin.id === 999999999 || admin.totalAccess) return false;
    if (admin.permissions == null) return false;

    const caps = admin.permissions.sections?.[AdminSectionEnum.ROUTES] || [];
    const structural =
        caps.includes(AdminCapabilityEnum.EDIT) || caps.includes(AdminCapabilityEnum.DELETE);

    return caps.includes(AdminCapabilityEnum.TEXT) && !structural;
};

export const SECTION_PATH: Record<AdminSectionEnum, string> = {
    [AdminSectionEnum.DASHBOARD]: '/',
    [AdminSectionEnum.CITIES]: '/cities',
    [AdminSectionEnum.QUESTS]: '/quests',
    [AdminSectionEnum.ROUTES]: '/routes',
    [AdminSectionEnum.PEOPLE]: '/people',
    [AdminSectionEnum.ATTRACTIONS]: '/attractions',
    [AdminSectionEnum.ESTABLISHMENTS]: '/establishments',
    [AdminSectionEnum.DISTRICTS]: '/districts',
    [AdminSectionEnum.FOODS]: '/foods',
    [AdminSectionEnum.AUTHORS]: '/authors',
    [AdminSectionEnum.VENUES]: '/venues',
    [AdminSectionEnum.ACTIVITIES]: '/activities',
    [AdminSectionEnum.ORDERS]: '/orders',
    [AdminSectionEnum.PURCHASES]: '/purchases',
    [AdminSectionEnum.REVIEWS]: '/reviews',
    [AdminSectionEnum.TRANSLATIONS]: '/translations',
    [AdminSectionEnum.QA]: '/qa',
    [AdminSectionEnum.USERS]: '/users',
};

/** Prefer `t('enums.capability.' + capability)` in UI; kept for non-React callers. */
export const capabilityLabelKey = (capability: AdminCapabilityEnum) =>
    `enums.capability.${capability}` as const;
